import { Chip, Typography } from '@material-ui/core'
import * as dateFns from 'date-fns'
import { HeaderGroup } from 'react-table'
import { DeleteRecord } from '../../components'
import { SelectFilterGeneric } from '../../components/ColumnsFiltersTable'
import { IColumn } from '../../components/BaseTable'
import When from '../../components/When'
import {
  CenarioDetail,
  CenarioMetadataResponse,
  TipoCenario,
  TipoCondicaoCenario
} from './types'
import SituacaoBadge from './SituacaoBadge'
import { doDiaCivil } from './utils/vigencia'

const MAX_CHIPS_VISIVEIS = 3

interface Props {
  onDelete: (id: string) => void
  authorization?: {
    canCreate?: boolean
    canDelete?: boolean
    canUpdate?: boolean
  }
  metadata: CenarioMetadataResponse | null
}

const formatData = (data?: string | null) =>
  data ? dateFns.format(doDiaCivil(data), 'dd/MM/yyyy') : ''

export const cenariosColumns = ({
  onDelete,
  authorization,
  metadata
}: Props): IColumn[] => {
  return [
    {
      Header: 'Código',
      accessor: 'codigo'
    },
    {
      Header: 'Tipo',
      accessor: 'tipo',
      Cell: (props: any) =>
        metadata?.tipos[props.value as TipoCenario]?.label ?? props.value
    },
    {
      Header: 'Descrição',
      accessor: 'descricao'
    },
    {
      Header: 'Condições',
      accessor: 'condicoes',
      sort: false,
      search: false,
      Cell: (props: any) => {
        const condicoes = props.value as CenarioDetail['condicoes']

        if (!condicoes?.length) {
          return (
            <Typography variant="body2" style={{ fontStyle: 'italic' }}>
              Sem condição — cenário padrão do tipo
            </Typography>
          )
        }

        const visiveis = condicoes.slice(0, MAX_CHIPS_VISIVEIS)
        const restantes = condicoes.length - visiveis.length

        return (
          <>
            {visiveis.map(condicao => (
              <Chip
                key={condicao.id}
                size="small"
                style={{ margin: 2 }}
                label={`${
                  metadata?.condicoes[condicao.tipo as TipoCondicaoCenario]
                    ?.label ?? condicao.tipo
                }: ${condicao.rotulo}`}
              />
            ))}
            {restantes > 0 && (
              <Chip
                size="small"
                style={{ margin: 2 }}
                label={`+${restantes}`}
              />
            )}
          </>
        )
      }
    },
    {
      Header: 'Vigência',
      accessor: 'dataInicio',
      sort: false,
      search: false,
      Cell: (props: any) => {
        const cenario = props.row.original as CenarioDetail | undefined

        if (!cenario) {
          return <></>
        }

        return (
          <>
            {formatData(cenario.dataInicio)} -{' '}
            {cenario.dataFim ? formatData(cenario.dataFim) : 'sem fim'}
          </>
        )
      }
    },
    {
      Header: 'Situação',
      accessor: 'ativo',
      sort: false,
      Filter: props =>
        SelectFilterGeneric({
          column: props.column as HeaderGroup<{}>,
          options: [
            { value: 'true', label: 'Ativo' },
            { value: 'false', label: 'Inativo' }
          ]
        }),
      Cell: (props: any) =>
        props.row.original ? (
          <SituacaoBadge cenario={props.row.original} />
        ) : (
          <></>
        )
    },
    {
      Header: '',
      accessor: 'actions',
      sort: false,
      selectable: false,
      defaultClick: false,
      Cell: (props: any) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {props.row.original && (
            <When value={authorization?.canUpdate} equals>
              <DeleteRecord
                title="Excluir cenário"
                message="Deseja realmente excluir este cenário? Essa ação não pode ser desfeita."
                onDelete={() => onDelete(props.row.original.id)}
              />
            </When>
          )}
        </div>
      )
    }
  ]
}
